import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { socket } from '../lib/socket.js';
import { useAuthStore } from '../store/authStore.js';

const STAFF_ROLES = ['mechanic', 'electrician', 'painter'];

/**
 * Toast global que notifica al mecánico/eléctrico/pintor cuando el cliente
 * aprueba o rechaza un presupuesto que él envió. El backend emite
 * `budget:response` al room user:<id>, en el que cada socket entra automáticamente.
 *
 * Solo se monta para roles staff (no admin/billing/client/reception) — los demás
 * tienen otros canales para enterarse.
 */
export default function BudgetResponseToast() {
  const { user } = useAuthStore();
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(ts => ts.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (!user || !STAFF_ROLES.includes(user.role)) return;

    if (!socket.connected) socket.connect();

    const onResponse = (payload) => {
      if (!payload || typeof payload !== 'object') return;
      // Solo mostrar si tiene la metadata completa (vino del room user:<id>, no del room order:<id>)
      if (!('approved' in payload) || !payload.id) return;
      if (payload.amount == null) return; // el evento de room order:<id> no trae amount

      const id = `${payload.id}-${Date.now()}`;
      setToasts(ts => [...ts, { id, ...payload }]);
      // Auto-dismiss en 12 segundos
      setTimeout(() => dismiss(id), 12000);
    };

    socket.on('budget:response', onResponse);
    return () => { socket.off('budget:response', onResponse); };
  }, [user, dismiss]);

  if (!user || !STAFF_ROLES.includes(user.role) || toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 16,
      right: 16,
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      maxWidth: 'calc(100vw - 32px)',
      width: 360,
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: t.approved ? 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(8,8,8,0.95))' : 'linear-gradient(135deg, rgba(232,64,64,0.18), rgba(8,8,8,0.95))',
          border: `1px solid ${t.approved ? 'rgba(34,197,94,0.4)' : 'rgba(232,64,64,0.4)'}`,
          color: '#fff',
          padding: '0.9rem 1rem',
          borderRadius: 10,
          backdropFilter: 'blur(14px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
          animation: 'slideIn .25s ease-out',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>
                {t.approved ? '✅ Cliente aprobó tu presupuesto' : '✗ Cliente rechazó tu presupuesto'}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#cbd5e1', lineHeight: 1.4 }}>
                ${Number(t.amount).toFixed(2)} · {t.description?.slice(0, 80)}{t.description?.length > 80 ? '…' : ''}
              </div>
              {!t.approved && t.rejection_reason && (
                <div style={{ fontSize: '0.75rem', color: '#fca5a5', marginTop: 6, fontStyle: 'italic' }}>
                  Razón: {t.rejection_reason}
                </div>
              )}
              <Link to={`/mechanic/order/${t.order_id}`}
                onClick={() => dismiss(t.id)}
                style={{ display: 'inline-block', marginTop: 8, fontSize: '0.78rem', color: t.approved ? '#22c55e' : '#fca5a5', fontWeight: 700, textDecoration: 'none' }}>
                Ver orden →
              </Link>
            </div>
            <button onClick={() => dismiss(t.id)}
              aria-label="Cerrar"
              style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1, padding: 0 }}>
              ×
            </button>
          </div>
        </div>
      ))}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(20px); opacity: 0; }
          to   { transform: translateX(0);     opacity: 1; }
        }
      `}</style>
    </div>
  );
}
